import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import "./TickerSavedBar.css";

export const SAVED_FILTERS = [
  { id: "all", label: "همه" },
  { id: "politics", label: "سیاسی" },
  { id: "economy", label: "اقتصادی" },
  { id: "society", label: "جامعه" },
  { id: "sports", label: "ورزشی" },
  { id: "tech", label: "تکنولوژی" },
  { id: "entertainment", label: "سرگرمی" },
  { id: "health", label: "سلامت" },
  { id: "car", label: "خودرو" },
  { id: "misc", label: "سایر" },
];

const DEFAULT_FILTER = SAVED_FILTERS[0].id;
const FILTER_IDS = SAVED_FILTERS.map(({ id }) => id);
const VISUAL_FILTERS = [...FILTER_IDS].reverse();
const SAVED_PAGER_PROGRESS_EVENT = "savedPagerProgress";

const isValidFilter = (filterId) => FILTER_IDS.includes(filterId);

const getSafeFilter = (filterId) =>
  isValidFilter(filterId) ? filterId : DEFAULT_FILTER;

const clampProgress = (progress) => {
  const numericProgress = Number(progress);

  if (!Number.isFinite(numericProgress)) return 0;

  return Math.max(
    0,
    Math.min(numericProgress, VISUAL_FILTERS.length - 1)
  );
};

const getFilterProgress = (filterId) => {
  const safeFilter = getSafeFilter(filterId);
  const index = VISUAL_FILTERS.indexOf(safeFilter);

  return index >= 0 ? index : 0;
};

const TickerSavedBar = ({
  activeFilter = DEFAULT_FILTER,
  onFilterChange,
}) => {
  const safeActiveFilter = getSafeFilter(activeFilter);

  const filtersRef = useRef(null);
  const pillRef = useRef(null);
  const buttonRefs = useRef({});
  const targetsRef = useRef([]);
  const progressRef = useRef(getFilterProgress(safeActiveFilter));
  const visibleFilterRef = useRef(safeActiveFilter);
  const measureRafRef = useRef(null);
  const scrollIntoViewRafRef = useRef(null);

  const keepFilterInView = useCallback((filterId, behavior = "smooth") => {
    const button = buttonRefs.current[filterId];
    if (!button) return;

    button.scrollIntoView({
      behavior,
      block: "nearest",
      inline: "center",
    });
  }, []);

  const scheduleKeepFilterInView = useCallback(
    (filterId, behavior = "smooth") => {
      if (typeof requestAnimationFrame === "undefined") {
        keepFilterInView(filterId, behavior);
        return;
      }

      if (scrollIntoViewRafRef.current !== null) {
        cancelAnimationFrame(scrollIntoViewRafRef.current);
      }

      scrollIntoViewRafRef.current = requestAnimationFrame(() => {
        scrollIntoViewRafRef.current = null;
        keepFilterInView(filterId, behavior);
      });
    },
    [keepFilterInView]
  );

  const applyPillProgress = useCallback((progressValue) => {
    const pill = pillRef.current;
    const targets = targetsRef.current;

    if (!pill || targets.length !== VISUAL_FILTERS.length) return;

    const progress = clampProgress(progressValue);
    const lowerIndex = Math.floor(progress);
    const upperIndex = Math.ceil(progress);
    const ratio = progress - lowerIndex;

    const from = targets[lowerIndex];
    const to = targets[upperIndex] || from;

    if (!from || !to) return;

    const width = from.width + (to.width - from.width) * ratio;
    const x = from.x + (to.x - from.x) * ratio;
    const pillLeft = x;
    const pillRight = x + width;

    pill.style.width = `${Math.max(0, width)}px`;
    pill.style.transform = `translate3d(${x}px, 0, 0)`;
    pill.classList.add("ticker-saved-bar__active-pill--ready");

    VISUAL_FILTERS.forEach((filterId, index) => {
      const button = buttonRefs.current[filterId];
      const target = targets[index];
      const activeText = button?.querySelector(
        ".ticker-saved-bar__filter-text--active"
      );

      if (!button || !target || !activeText) return;

      const overlapLeft = Math.max(0, pillLeft - target.x);
      const overlapRight = Math.min(target.width, pillRight - target.x);
      const hasOverlap = overlapRight > overlapLeft;
      const rightInset = Math.max(0, target.width - overlapRight);

      activeText.style.opacity = hasOverlap ? "1" : "0";
      activeText.style.clipPath = hasOverlap
        ? `inset(0 ${rightInset}px 0 ${overlapLeft}px)`
        : "inset(0 100% 0 0)";
    });
  }, []);

  const measureTargets = useCallback(() => {
    const filtersNode = filtersRef.current;
    if (!filtersNode) return;

    const parentRect = filtersNode.getBoundingClientRect();

    const targets = VISUAL_FILTERS.map((filterId) => {
      const button = buttonRefs.current[filterId];
      if (!button) return null;

      const rect = button.getBoundingClientRect();

      return {
        width: rect.width,
        x: rect.left - parentRect.left + filtersNode.scrollLeft,
      };
    });

    if (targets.some((target) => !target)) return;

    targetsRef.current = targets;
    applyPillProgress(progressRef.current);
  }, [applyPillProgress]);

  const scheduleMeasureTargets = useCallback(() => {
    if (typeof requestAnimationFrame === "undefined") {
      measureTargets();
      return;
    }

    if (measureRafRef.current !== null) return;

    measureRafRef.current = requestAnimationFrame(() => {
      measureRafRef.current = null;
      measureTargets();
    });
  }, [measureTargets]);

  useLayoutEffect(() => {
    progressRef.current = getFilterProgress(safeActiveFilter);
    visibleFilterRef.current = safeActiveFilter;

    measureTargets();
    scheduleKeepFilterInView(safeActiveFilter, "smooth");

    const filtersNode = filtersRef.current;

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleMeasureTargets)
        : null;

    if (observer && filtersNode) {
      observer.observe(filtersNode);

      Object.values(buttonRefs.current).forEach((button) => {
        if (button) observer.observe(button);
      });
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", scheduleMeasureTargets);
    }

    filtersNode?.addEventListener("scroll", scheduleMeasureTargets, {
      passive: true,
    });

    return () => {
      filtersNode?.removeEventListener("scroll", scheduleMeasureTargets);

      if (observer) {
        observer.disconnect();
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", scheduleMeasureTargets);
      }

      if (
        measureRafRef.current !== null &&
        typeof cancelAnimationFrame !== "undefined"
      ) {
        cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }

      if (
        scrollIntoViewRafRef.current !== null &&
        typeof cancelAnimationFrame !== "undefined"
      ) {
        cancelAnimationFrame(scrollIntoViewRafRef.current);
        scrollIntoViewRafRef.current = null;
      }
    };
  }, [
    measureTargets,
    safeActiveFilter,
    scheduleKeepFilterInView,
    scheduleMeasureTargets,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleProgress = (event) => {
      const nextProgress = clampProgress(event.detail?.progress);

      progressRef.current = nextProgress;
      applyPillProgress(nextProgress);

      const nextFilter = VISUAL_FILTERS[Math.round(nextProgress)];

      if (nextFilter && visibleFilterRef.current !== nextFilter) {
        visibleFilterRef.current = nextFilter;
        scheduleKeepFilterInView(nextFilter, "smooth");
      }
    };

    window.addEventListener(SAVED_PAGER_PROGRESS_EVENT, handleProgress);

    return () => {
      window.removeEventListener(SAVED_PAGER_PROGRESS_EVENT, handleProgress);
    };
  }, [applyPillProgress, scheduleKeepFilterInView]);

  const handleFilterClick = useCallback(
    (filterId) => {
      if (activeFilter === filterId) return;
      onFilterChange?.(filterId);
    },
    [activeFilter, onFilterChange]
  );

  return (
    <div className="ticker-saved-bar">
      <div className="ticker-saved-bar__capsule">
        <span className="ticker-saved-bar__title">ذخیره‌شده‌ها</span>

        <div className="ticker-saved-bar__filters" ref={filtersRef}>
          <span
            ref={pillRef}
            className="ticker-saved-bar__active-pill"
            aria-hidden="true"
          />

          {SAVED_FILTERS.map(({ id, label }) => {
            const isActive = safeActiveFilter === id;

            return (
              <button
                key={id}
                ref={(node) => {
                  if (node) {
                    buttonRefs.current[id] = node;
                  } else {
                    delete buttonRefs.current[id];
                  }
                }}
                type="button"
                className={`ticker-saved-bar__filter ${
                  isActive ? "active" : ""
                }`}
                aria-pressed={isActive}
                onClick={() => handleFilterClick(id)}
              >
                <span className="ticker-saved-bar__filter-text">
                  {label}
                </span>

                <span
                  className="ticker-saved-bar__filter-text ticker-saved-bar__filter-text--active"
                  aria-hidden="true"
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TickerSavedBar;